import { FAQCategory, FAQItem, ContactSubject, SupportPageData } from '../types/support.types'

export const FAQ_CATEGORIES: FAQCategory[] = [
  {
    id: 'onboarding-basics',
    name: 'Onboarding Basics',
    description: 'Getting started with StratIQX intelligence services',
    displayOrder: 1
  },
  {
    id: 'workflow-steps',
    name: 'Workflow Steps',
    description: 'Understanding our 9-step intelligence process',
    displayOrder: 2
  },
  {
    id: 'technical-issues',
    name: 'Technical Issues',
    description: 'Troubleshooting and technical support',
    displayOrder: 3
  },
  {
    id: 'billing-access',
    name: 'Billing & Access',
    description: 'Payment, pricing, and account access',
    displayOrder: 4
  }
]

export const FAQ_ITEMS: FAQItem[] = [
  // Onboarding Basics
  {
    id: 'what-is-stratiqx',
    question: 'What is StratIQX and how does it work?',
    answer: 'StratIQX provides AI-powered strategic intelligence services that transform complex market data into clear strategic advantage. Our platform combines advanced AI agents with human expertise to deliver comprehensive market insights, competitive analysis, and strategic recommendations tailored to your industry.',
    category: 'onboarding-basics',
    keywords: ['stratiqx', 'intelligence', 'ai', 'market data', 'strategic'],
    priority: 1
  },
  {
    id: 'how-to-get-started',
    question: 'How do I get started with StratIQX?',
    answer: 'Getting started is simple: 1) Click "Get Intelligence Report" to begin our 9-step onboarding process, 2) Select your industry and business profile, 3) Configure your intelligence preferences, 4) Choose your service package, 5) Complete payment, and 6) Receive your first intelligence report within 4 hours.',
    category: 'onboarding-basics',
    keywords: ['get started', 'onboarding', '8-step', 'process', 'begin'],
    priority: 2
  },
  {
    id: 'industry-support',
    question: 'Which industries does StratIQX support?',
    answer: 'We currently support automotive services, construction equipment, heavy equipment, transportation & logistics, manufacturing, professional services, healthcare & dental, aviation & aerospace, technology consulting, and food service franchises. Each industry receives customized intelligence frameworks and specialized analysis.',
    category: 'onboarding-basics',
    keywords: ['industries', 'automotive', 'construction', 'manufacturing', 'healthcare', 'technology'],
    priority: 3
  },
  {
    id: 'onboarding-time',
    question: 'How long does the onboarding process take?',
    answer: 'The complete onboarding process typically takes about 25 minutes. Each of the 9 steps is designed to be quick and intuitive: Industry Selection (2 mins), Business Profile (3 mins), Revenue Intelligence (4 mins), Supply Chain Intelligence (3 mins), Competitive Intelligence (4 mins), Service Selection (3 mins), Intelligence Delivery (2 mins), Payment & Activation (3 mins), and Intelligence Summary (3 mins).',
    category: 'onboarding-basics',
    keywords: ['time', 'duration', '25 minutes', 'quick', 'steps'],
    priority: 4
  },
  {
    id: 'authentication-required',
    question: 'Do I need to create an account to use StratIQX?',
    answer: 'Yes, you will need to authenticate with your email address and verify via OTP (one-time password) to ensure secure access to your intelligence reports and maintain data privacy. This also allows you to manage your intelligence preferences and delivery settings.',
    category: 'onboarding-basics',
    keywords: ['account', 'authentication', 'email', 'otp', 'login', 'security'],
    priority: 5
  },

  // Workflow Steps
  {
    id: 'industry-selection-step',
    question: 'What happens in the Industry Selection step?',
    answer: 'In Step 1, you select your primary industry from our supported sectors. This determines the intelligence frameworks, data sources, and analysis methodologies we use for your reports. If your industry isn\'t listed, you can request a custom industry configuration.',
    category: 'workflow-steps',
    keywords: ['step 1', 'industry selection', 'frameworks', 'custom industry'],
    priority: 1
  },
  {
    id: 'business-profile-step',
    question: 'What information do I provide in the Business Profile step?',
    answer: 'Step 2 captures your company size, revenue range, geographic markets, and strategic priorities. This helps us tailor intelligence reports to your specific business context and ensure relevant insights for your scale and market position.',
    category: 'workflow-steps',
    keywords: ['step 2', 'business profile', 'company size', 'revenue', 'geographic markets'],
    priority: 2
  },
  {
    id: 'revenue-intelligence-step',
    question: 'What is Revenue Intelligence and how is it configured?',
    answer: 'Step 3 focuses on revenue optimization opportunities. You define your revenue streams, pricing strategies, and growth targets. We analyze market pricing trends, demand patterns, and revenue optimization opportunities specific to your business model.',
    category: 'workflow-steps',
    keywords: ['step 3', 'revenue intelligence', 'pricing', 'revenue streams', 'optimization'],
    priority: 3
  },
  {
    id: 'supply-chain-step',
    question: 'How does Supply Chain Intelligence help my business?',
    answer: 'Step 4 maps your supply chain dependencies and risk factors. We monitor supplier stability, raw material costs, logistics disruptions, and alternative sourcing options to help you maintain operational resilience and cost efficiency.',
    category: 'workflow-steps',
    keywords: ['step 4', 'supply chain', 'suppliers', 'logistics', 'risk factors'],
    priority: 4
  },
  {
    id: 'competitive-intelligence-step',
    question: 'How does Competitive Intelligence configuration work?',
    answer: 'In Step 5, you specify your key competitors, competitive threats, and market positioning goals. Our AI agents will monitor these competitors and provide insights on strategic moves, pricing changes, product launches, and market share shifts.',
    category: 'workflow-steps',
    keywords: ['step 5', 'competitive intelligence', 'competitors', 'monitoring', 'insights'],
    priority: 5
  },
  {
    id: 'service-selection-step',
    question: 'How do I choose the right service package in Step 6?',
    answer: 'Step 6 presents different intelligence service packages from free quick reports to comprehensive strategic analysis. Each package shows delivery timeframes, report depth, and pricing. You can select based on your business needs and budget.',
    category: 'workflow-steps',
    keywords: ['step 6', 'service selection', 'packages', 'free', 'pricing', 'reports'],
    priority: 6
  },
  {
    id: 'delivery-preferences-step',
    question: 'How do I configure Intelligence Delivery preferences?',
    answer: 'Step 7 lets you set your email delivery preferences and report format options. You can customize how you receive your intelligence reports based on your selected service package.',
    category: 'workflow-steps',
    keywords: ['step 7', 'delivery', 'email', 'report formats', 'preferences'],
    priority: 7
  },
  {
    id: 'payment-activation-step',
    question: 'What happens in the Payment & Activation step?',
    answer: 'Step 8 handles secure payment processing for premium services or instant activation for free services. Payment is processed through Stripe with bank-level security. Your intelligence reports will be delivered via email within 4 hours.',
    category: 'workflow-steps',
    keywords: ['step 8', 'payment', 'activation', 'stripe', 'security', 'email delivery'],
    priority: 8
  },
  {
    id: 'summary-review-step',
    question: 'What is reviewed in the Intelligence Summary step?',
    answer: 'Step 9 provides a complete overview of your intelligence profile configuration. You can review all settings, verify service activation, and confirm your email delivery preferences for strategic intelligence reports.',
    category: 'workflow-steps',
    keywords: ['step 9', 'summary', 'review', 'activation', 'email delivery'],
    priority: 9
  },

  // Technical Issues
  {
    id: 'browser-compatibility',
    question: 'Which browsers are supported?',
    answer: 'StratIQX works best with modern browsers including Chrome (recommended), Firefox, Safari, and Edge. Please ensure you have JavaScript enabled and are using an up-to-date browser version for optimal performance.',
    category: 'technical-issues',
    keywords: ['browser', 'chrome', 'firefox', 'safari', 'edge', 'javascript'],
    priority: 1
  },
  {
    id: 'otp-not-received',
    question: 'I\'m not receiving the OTP verification code',
    answer: 'If you don\'t receive the OTP code: 1) Check your spam/junk folder, 2) Ensure you entered the correct email address, 3) Wait 60 seconds before requesting a new code, 4) Try a different email address if issues persist. Contact support if the problem continues.',
    category: 'technical-issues',
    keywords: ['otp', 'verification', 'email', 'spam', 'code'],
    priority: 2
  },
  {
    id: 'onboarding-stuck',
    question: 'The onboarding process seems stuck or won\'t progress',
    answer: 'If you experience issues during onboarding: 1) Refresh the page and try again, 2) Clear your browser cache and cookies, 3) Ensure all required fields are completed, 4) Try using a different browser or incognito mode. Your progress is automatically saved.',
    category: 'technical-issues',
    keywords: ['stuck', 'onboarding', 'refresh', 'cache', 'progress saved'],
    priority: 3
  },
  {
    id: 'mobile-experience',
    question: 'Can I complete onboarding on mobile devices?',
    answer: 'Yes, StratIQX is fully responsive and optimized for mobile devices. You can complete the entire onboarding process on smartphones and tablets. For the best experience with detailed forms, we recommend using a tablet or desktop.',
    category: 'technical-issues',
    keywords: ['mobile', 'responsive', 'smartphone', 'tablet', 'desktop'],
    priority: 4
  },

  // Billing & Access
  {
    id: 'pricing-plans',
    question: 'What are the pricing plans and payment options?',
    answer: 'Pricing varies based on your selected intelligence services, delivery frequency, and business size. You\'ll see transparent pricing during the onboarding process before payment. We accept major credit cards and offer monthly or annual billing cycles.',
    category: 'billing-access',
    keywords: ['pricing', 'payment', 'credit cards', 'monthly', 'annual'],
    priority: 1
  },
  {
    id: 'payment-security',
    question: 'Is my payment information secure?',
    answer: 'Yes, all payment processing is handled through Stripe, a PCI-compliant payment processor. We never store your credit card information on our servers. All transactions are encrypted and secure.',
    category: 'billing-access',
    keywords: ['payment security', 'stripe', 'pci compliant', 'encrypted'],
    priority: 2
  },
  {
    id: 'access-reports',
    question: 'How do I receive my intelligence reports?',
    answer: 'After completing onboarding and payment, your intelligence reports will be delivered via secure email within 4 hours. Reports are sent as PDF attachments to your registered email address. This is currently our only delivery method - no dashboard access is available yet.',
    category: 'billing-access',
    keywords: ['reports', 'email', 'delivery', 'PDF', 'attachments'],
    priority: 3
  }
]

export const CONTACT_SUBJECTS: ContactSubject[] = [
  {
    id: 'onboarding-question',
    label: 'Onboarding Question',
    value: 'onboarding-question'
  },
  {
    id: 'technical-issue',
    label: 'Technical Issue',
    value: 'technical-issue'
  },
  {
    id: 'billing-question',
    label: 'Billing Question',
    value: 'billing-question'
  },
  {
    id: 'feature-request',
    label: 'Feature Request',
    value: 'feature-request'
  },
  {
    id: 'general-inquiry',
    label: 'General Inquiry',
    value: 'general-inquiry'
  }
]

export const SUPPORT_PAGE_DATA: SupportPageData = {
  categories: FAQ_CATEGORIES,
  faqs: FAQ_ITEMS,
  contactSubjects: CONTACT_SUBJECTS
}